/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import NumberDoc from '../../../domain/models/NumberDoc';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getNumbers from '../../../domain/use_cases/numbers/getNumbers';
import NumberOptions from '../../../domain/types/NumberOptions';

const NumbersContext = createContext({
  count: 0,
  assignedCount: 0,
  availableCount: 0,
  items: [] as NumberDoc[],
  selected: null as NumberDoc | null,
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  fetch: (_options?: NumberOptions) => {},
  fetchNext: (_options?: NumberOptions) => {},
  select: (_: NumberDoc) => {},
  reset: () => {},
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
});

export const NUMBERS_FETCH_LIMIT = 10;

export const NumbersProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [count, setCount] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [items, setItems] = useState([] as NumberDoc[]);
  const [selected, setSelected] = useState(null as NumberDoc | null);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);

  const fetch = async (options?: NumberOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumbers(NUMBERS_FETCH_LIMIT, undefined, options);
      setItems(useCaseResponse.numbers);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      if (useCaseResponse.availabilityCounts) {
        setAssignedCount(useCaseResponse.availabilityCounts.assigned);
        setAvailableCount(useCaseResponse.availabilityCounts.available);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async (options?: NumberOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumbers(NUMBERS_FETCH_LIMIT, lastVisible, options);
      setItems([...items, ...useCaseResponse.numbers]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const select = (number: NumberDoc) => {
    setSelected(number);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const reset = () => {
    setItems([]);
    setCount(0);
    setSelected(null);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setStatus(ContextStatus.IDLE);
    setSelectionStatus(ContextStatus.IDLE);
  };

  return (
    <NumbersContext.Provider
      value={{
        count,
        assignedCount,
        availableCount,
        items,
        selected,
        lastVisible,
        fetch,
        fetchNext,
        select,
        reset,
        status,
        selectionStatus,
      }}
    >
      {children}
    </NumbersContext.Provider>
  );
};

export default NumbersContext;
